import { idsDoBanco } from './bancos'
import type { MapaEstados } from './estadoPecas'
import type { PerfilVeiculo } from './perfilVeiculo'
import { prateleiraTraseira } from './pontosCinto'

/**
 * O que a saída pela traseira pede de cada carroceria.
 *
 * No hatch a tampa é o conjunto vidro + chapa: levantá-la abre o vão inteiro,
 * e o que sobra é rebater os encostos traseiros — para a frente: o banco de
 * trás deita sobre o assento, não reclina. No sedan o vidro traseiro é fixo e
 * há uma prateleira entre ele e o encosto: o porta-malas abre, mas a cabine
 * continua fechada por trás. Retirar o vidro, soltar a prateleira e rebater os
 * bancos não basta: a divisória do porta-malas, atrás do encosto, segue no
 * caminho (ver `divisoriaPortaMalas`).
 *
 * Mora aqui, e não nas telas, para que o procedimento marcado pelo aluno e a
 * animação automática da retirada produzam exatamente as mesmas peças em cena.
 */
export interface AcessoTraseiro {
  /** retira o vidro traseiro e solta a prateleira (só existe no sedan) */
  vidroEPrateleira: boolean
  /** rebate o encosto de todos os bancos traseiros */
  bancosTraseiros: boolean
}

/** O vidro traseiro só é uma peça separada, que precisa sair, no sedan. */
export const vidroTraseiroEFixo = (perfil: PerfilVeiculo): boolean => perfil.volumeTraseiro === 'sedan'

/** Estado de peças correspondente ao acesso pela traseira já preparado. */
export function estadosDoAcessoTraseiro(perfil: PerfilVeiculo, acesso: AcessoTraseiro): MapaEstados {
  const mapa: Record<string, 'aberta' | 'removida'> = {}
  if (acesso.vidroEPrateleira && vidroTraseiroEFixo(perfil)) {
    mapa.vidroTraseiro = 'removida'
    mapa.prateleiraTraseira = 'removida'
  }
  if (acesso.bancosTraseiros) {
    for (const ancora of perfil.ancoras.filter((a) => a.fila === 'traseira')) {
      mapa[idsDoBanco(ancora).encosto] = 'aberta'
    }
  }
  return mapa
}

/** Instante (fração da retirada) em que a animação retira o vidro e solta a prateleira: dentro da abertura dos acessos. */
export const TEMPO_VIDRO_TRASEIRO = 0.04

/**
 * O acesso que a animação da retirada já fez no instante `tempo`.
 *
 * Os bancos não entram aqui: na animação eles seguem frações próprias (primeiro
 * o encosto de trás deita para a frente, depois o da frente reclina para trás —
 * ver `fracaoRebaterBancosTraseiros` em `rotasExtracao`).
 */
export function acessoTraseiroNoTempo(tempo: number): AcessoTraseiro {
  return { vidroEPrateleira: tempo >= TEMPO_VIDRO_TRASEIRO, bancosTraseiros: false }
}

/**
 * Divisória do porta-malas: o suporte fixo, atrás do banco de trás do sedan, que
 * separa o porta-malas da cabine e sustenta o encosto.
 *
 * Rebater o encosto para a frente não a tira do lugar: é ela que faz a retirada
 * a 0° pelo porta-malas ser inviável no sedan sem técnica própria. O hatch não
 * tem — a tampa abre o porta-malas junto com o vidro, e o banco rebate sobre o
 * assento deixando a área de carga contínua.
 */
export interface DivisoriaPortaMalas {
  /** posição longitudinal do painel */
  z: number
  yBase: number
  yTopo: number
  /** meia-largura útil */
  meiaLargura: number
  espessura: number
}

export function divisoriaPortaMalas(perfil: PerfilVeiculo): DivisoriaPortaMalas | null {
  const prateleira = prateleiraTraseira(perfil)
  if (!prateleira) return null
  return {
    // rente à face de trás do encosto, onde a prateleira começa
    z: prateleira.zFrente + 0.03,
    yBase: perfil.pisoY + 0.12,
    yTopo: prateleira.yTopo,
    meiaLargura: perfil.largura / 2 - 0.12,
    espessura: 0.03,
  }
}
